import { isEmpty } from 'lodash'
import { useEffect, useState } from 'react'

import { PortfolioViewState } from 'enums/PortfolioViewState'
import { Portfolio } from 'types'

import { paveApi } from 'api'
import Loader from 'components/common/Loader/Loader'
import ProjectedFundamentalsInfoModal from 'components/partials/Portfolio/V2/Modals/ProjectedFundamentalsInfoModal'
import PDSAssetList from 'designSystem/components/PDSAssetList/PDSAssetList'
import PDSBox from 'designSystem/components/PDSBox/PDSBox'
import PDSButton from 'designSystem/components/PDSButton/PDSButton'
import PDSCard from 'designSystem/components/PDSCard/PDSCard'
import PDSCardHeader from 'designSystem/components/PDSCardHeader/PDSCardHeader'
import PDSComparisonTable from 'designSystem/components/PDSComparisonTable/PDSComparisonTable'
import PDSFlexBox from 'designSystem/components/PDSFlexBox/PDSFlexBox'
import PDSHeader from 'designSystem/components/PDSHeader/PDSHeader'
import PDSIcon from 'designSystem/components/PDSIcon/PDSIcon'
import PDSSubCard from 'designSystem/components/PDSSubCard/PDSSubCard'
import PDSText from 'designSystem/components/PDSText/PDSText'
import { COLOR } from 'designSystem/foundations/constants/color'
import { SIZE } from 'designSystem/foundations/constants/size'
import { formatDateMMDDYYYY, getNextDate } from 'utils/dateUtil'
import {
  formatBreakdowns,
  formatHighlightsTableData,
  formatTopHoldings,
  getIsTradesScheduled,
  getNeedsTradesUpdate,
  getPortfolioViewState,
  shouldLockPortfolio,
} from 'utils/portfolioUtil'

import PortfolioBreakdownsChart from './Allocation/PortfolioBreakdowns'
import PortfolioViewSuggestedTradesSlideIn from './PortfolioViewSuggestedTradesSlideIn'

type Props = {
  portfolio: Portfolio
}

const TABLE_HEADERS = [
  {
    label: 'Trailing Price/Earnings',
  },
  {
    label: 'Price/Book',
  },
  {
    label: 'Dividend Yield',
  },
  {
    label: 'Annualized Dividend Income',
  },
]

const ITEM_CUSTOM_HEADERS: { [key: string]: any } = {
  Current: {
    render: () => {
      return (
        <PDSText color={COLOR.textSecondary} weight="semibold">
          Current
        </PDSText>
      )
    },
  },
  Optimized: {
    render: () => {
      return (
        <PDSText color={COLOR.purple400} weight="semibold">
          Optimized
        </PDSText>
      )
    },
  },
  Benchmark: {
    render: () => {
      return (
        <PDSText color={COLOR.blue400} weight="semibold">
          Benchmark
        </PDSText>
      )
    },
  },
}

export default function PortfolioAllocationCard({ portfolio }: Props) {
  const [showModal, setShowModal] = useState(false)
  const [showSlideIn, setShowSlideIn] = useState(false)

  const {
    id: portfolioId,
    daysUntilSuggestions,
    isInvestmentPreferenceCompleted,
    isThirdPartyManaged,
    isTrading,
    lastTradedAt,
    suggestedTrades,
  } = portfolio ?? {}

  const [getPortfolioStats, { isFetching, data }] =
    paveApi.useLazyGetPortfolioStatsQuery()

  useEffect(() => {
    // Timeout prevents a race condition after updating holdings manually
    setTimeout(() => getPortfolioStats(portfolioId), 500)
  }, [getPortfolioStats, portfolioId])

  const portfolioViewState = getPortfolioViewState(portfolio)
  const showCardContent = portfolioViewState === PortfolioViewState.NORMAL
  const topHoldings = formatTopHoldings(data ?? [])
  const highlights = formatHighlightsTableData(data ?? []).map((highlight) => {
    const { label, rows } = highlight
    return {
      header: ITEM_CUSTOM_HEADERS[label] ?? label,
      rows,
    }
  })
  const breakdowns = formatBreakdowns(data ?? [])

  const isLocked = shouldLockPortfolio(portfolio)
  const showViewSuggestedTradesButton =
    !isEmpty(suggestedTrades) && !isTrading && showCardContent
  const showLastTraded = !!lastTradedAt
  const showNextTrades =
    !!daysUntilSuggestions && !showViewSuggestedTradesButton

  // Trading Content
  const isTradesScheduled = getIsTradesScheduled(portfolio?.tradingStatus)
  const isTradesUpdateNeeded = getNeedsTradesUpdate(
    portfolio?.suggestedTradesStatus
  )
  const isWellPositioned = isEmpty(suggestedTrades) && !isTrading

  // This may not be needed
  const disableSuggestedTrades =
    isLocked || isThirdPartyManaged || isTradesUpdateNeeded

  const top5HoldingsWidth = `${100 / topHoldings.length}%`

  let tradingContent = null
  if (isInvestmentPreferenceCompleted) {
    if (isTradesScheduled) {
      tradingContent = 'Trades are scheduled.'
    } else if (isTradesUpdateNeeded) {
      tradingContent =
        'Your portfolio is in the process of building new suggested trades.'
    } else if (isTrading) {
      tradingContent = 'Your portfolio is currently being traded.'
    } else if (isWellPositioned) {
      tradingContent =
        'Your portfolio is currently well positioned for market conditions.'
    }
  }

  return (
    <>
      <PDSCard>
        <PDSCardHeader>
          <PDSFlexBox alignItems="center" justifyContent="space-between">
            <PDSHeader variant="h4">Portfolio Allocation</PDSHeader>
            {showViewSuggestedTradesButton && (
              <PDSButton
                disabled={disableSuggestedTrades}
                onClick={() => setShowSlideIn(true)}
                testId="view-suggested-trades"
                text="View Suggested Trades"
              />
            )}
          </PDSFlexBox>
        </PDSCardHeader>
        {showCardContent && (
          <PDSBox>
            <PDSFlexBox
              justifyContent="space-between"
              marginTop={SIZE.s8}
              marginBottom={SIZE.s16}
            >
              <PDSFlexBox alignItems="center" gap={SIZE.s4}>
                {showLastTraded && (
                  <>
                    <PDSIcon name="clock" />
                    <PDSText color={COLOR.textSecondary} weight="medium">
                      Last trades placed on:{' '}
                      {formatDateMMDDYYYY(new Date(lastTradedAt))}
                    </PDSText>
                  </>
                )}
              </PDSFlexBox>
              {showNextTrades && (
                <PDSFlexBox alignItems="center" gap={SIZE.s4}>
                  <PDSIcon color={COLOR.textAction} name="sparkles" />
                  <PDSText color={COLOR.textAction} weight="medium">
                    Next suggested trades available on:{' '}
                    {formatDateMMDDYYYY(getNextDate(daysUntilSuggestions))}
                  </PDSText>
                </PDSFlexBox>
              )}
            </PDSFlexBox>

            {tradingContent && (
              <PDSBox>
                <PDSText>{tradingContent}</PDSText>
              </PDSBox>
            )}

            {isFetching || !data ? (
              <PDSFlexBox
                alignItems="center"
                flexDirection="column"
                height="20rem"
                justifyContent="center"
              >
                <Loader />
                <PDSText marginTop="1rem" size="lg">
                  Loading Stats...
                </PDSText>
              </PDSFlexBox>
            ) : (
              <PDSBox>
                {!isEmpty(breakdowns) && (
                  <PortfolioBreakdownsChart
                    breakdowns={breakdowns}
                    benchmarkName={portfolio?.benchmark?.name || ''}
                  />
                )}

                {!isEmpty(highlights) && (
                  <PDSBox marginTop={SIZE.s32}>
                    <PDSComparisonTable
                      headers={TABLE_HEADERS}
                      items={highlights}
                      title="Fundamentals"
                    />
                    <PDSSubCard
                      alignItems="center"
                      display="flex"
                      gap={SIZE.s16}
                      marginTop={SIZE.s12}
                      justifyContent="space-between"
                    >
                      <PDSBox maxWidth="35rem">
                        <PDSText
                          color={COLOR.textSecondary}
                          size="sm"
                          weight="semibold"
                          variant="p"
                        >
                          <b>Note:</b> References to dividends, yield, or income
                          potential are based on current market conditions and
                          are subject to change without notice.
                        </PDSText>
                      </PDSBox>
                      <PDSButton
                        size="sm"
                        text="Learn more"
                        onClick={() => setShowModal(true)}
                        variant="tertiary"
                      />
                    </PDSSubCard>
                  </PDSBox>
                )}

                {!isEmpty(topHoldings) && (
                  <PDSBox marginTop={SIZE.s32}>
                    <PDSHeader variant="h3">Top 5 Holdings</PDSHeader>
                    <PDSFlexBox gap={SIZE.s24} marginTop={SIZE.s24}>
                      {topHoldings.map(({ label, assets }) => {
                        const titleColor =
                          label === 'Optimized'
                            ? COLOR.purple400
                            : label === 'Benchmark'
                            ? COLOR.blue400
                            : COLOR.textSecondary

                        return (
                          <PDSFlexBox key={label} width={top5HoldingsWidth}>
                            <PDSAssetList
                              assets={assets}
                              title={label}
                              titleColor={titleColor}
                            />
                          </PDSFlexBox>
                        )
                      })}
                    </PDSFlexBox>
                  </PDSBox>
                )}
              </PDSBox>
            )}
          </PDSBox>
        )}
      </PDSCard>
      <ProjectedFundamentalsInfoModal
        onClose={() => setShowModal(false)}
        showModal={showModal}
      />
      <PortfolioViewSuggestedTradesSlideIn
        onClose={() => setShowSlideIn(false)}
        originalPortfolio={portfolio}
        showSlideIn={showSlideIn}
      />
    </>
  )
}
